import { useEffect, useState } from "react";
import api, { fmtDate, fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Warning, Plus, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", sms_carrier: "", email: "", password: "", service_types: "", rate: "", rate_basis: "per_visit" };

const Wrapper = ({ embedded, children }) => (embedded ? <>{children}</> : <Layout title="Crew roster">{children}</Layout>);

export default function Roster({ embedded = false }) {
  const [subs, setSubs] = useState(null);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [docForm, setDocForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [eform, setEform] = useState(EMPTY);
  const [teams, setTeams] = useState([]);
  const [teamForm, setTeamForm] = useState({ name: "", member_ids: [] });
  const [teamEditId, setTeamEditId] = useState(null);
  const [schedId, setSchedId] = useState(null);

  const sendSchedule = async (s, channel) => {
    try {
      const r = await api.post(`/subcontractors/${s.id}/send-schedule`, { channel, days: 7 });
      toast.success(`Schedule sent by ${r.data.sent.join(" + ")} — ${r.data.assigned} assigned job${r.data.assigned === 1 ? "" : "s"}, ${r.data.offers} open offer${r.data.offers === 1 ? "" : "s"}`);
      setSchedId(null);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => {
    api.get("/subcontractors").then((r) => setSubs(r.data)).catch(() => toast.error("Could not load roster"));
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/subcontractors", {
        ...form, rate: Number(form.rate || 0),
        service_types: form.service_types.split(",").map((s) => s.trim()).filter(Boolean),
      });
      if (form.email && !form.password && r.data.invite_sent === false) toast.error("Crew added, but the setup email could not be sent — check the address, or use the card's invite button to retry");
      else toast.success("Subcontractor added");
      setShowAdd(false); setForm(EMPTY); load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const toggle = async (s) => {
    await api.patch(`/subcontractors/${s.id}`, { status: s.status === "active" ? "inactive" : "active" });
    load();
  };

  const startEdit = (s) => {
    setEditId(editId === s.id ? null : s.id);
    setEform({ name: s.name, phone: s.phone || "", sms_carrier: s.sms_carrier || "", email: s.email || "", password: "", service_types: (s.service_types || []).join(", "), rate: String(s.rate ?? ""), rate_basis: s.rate_basis || "per_visit", weekly: (s.availability && s.availability.weekly) || {} });
  };

  const saveEdit = async (s) => {
    try {
      await api.patch(`/subcontractors/${s.id}`, {
        name: eform.name, phone: eform.phone, sms_carrier: eform.sms_carrier, email: eform.email,
        rate: Number(eform.rate || 0), rate_basis: eform.rate_basis,
        service_types: eform.service_types.split(",").map((x) => x.trim()).filter(Boolean),
      });
      if (eform.weekly) await api.patch(`/subcontractors/${s.id}/availability`, { weekly: eform.weekly });
      if (eform.password) await api.post(`/subcontractors/${s.id}/reset-password`, { password: eform.password });
      toast.success(eform.password ? "Crew member updated — login ready to share" : "Crew member updated");
      setEditId(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const emailLink = async (s) => {
    try {
      const r = await api.post(`/subcontractors/${s.id}/reset-password`, {});
      if (r.data.email_sent === false) {
        toast.error(`Couldn't send to ${s.email} — check the address, or set a password directly via Edit`);
      } else {
        toast.success(r.data.method === "email_invite" ? `Setup link sent to ${s.email} — good for 3 days` : `Reset link emailed to ${s.email} (expires in 1 hour)`);
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveTeam = async (e) => {
    e.preventDefault();
    try {
      if (teamEditId) await api.patch(`/teams/${teamEditId}`, teamForm);
      else await api.post("/teams", teamForm);
      toast.success(teamEditId ? "Team updated" : "Team created");
      setTeamForm({ name: "", member_ids: [] }); setTeamEditId(null); load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const deleteTeam = async (tid) => {
    try { await api.delete(`/teams/${tid}`); toast.success("Team deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const uploadDoc = async (sid) => {
    const d = docForm[sid] || {};
    if (!d.type || !d.data) return toast.error("Pick a type and file");
    try {
      await api.post(`/subcontractors/${sid}/documents`, { type: d.type, filename: d.filename, data: d.data, expiry_date: d.expiry_date || null });
      toast.success("Document saved");
      setDocForm({ ...docForm, [sid]: {} });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const pickFile = (sid, f) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setDocForm((p) => ({ ...p, [sid]: { ...(p[sid] || {}), filename: f.name, data: r.result } }));
    r.readAsDataURL(f);
  };

  return (
    <Wrapper embedded={embedded}>
      <button data-testid="add-sub-btn" onClick={() => setShowAdd(!showAdd)} className="mb-4 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
        <Plus size={20} weight="bold" /> Add subcontractor
      </button>
      {showAdd && (
        <form onSubmit={add} className="card-flat mb-6 space-y-3 p-4" data-testid="add-sub-form">
          <input data-testid="sub-name" className="field-input w-full" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input data-testid="sub-phone" className="field-input w-full" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          {form.phone && (
            <select data-testid="sub-carrier" className="field-input w-full" value={form.sms_carrier}
              onChange={(e) => setForm({ ...form, sms_carrier: e.target.value })}>
              <option value="">Carrier for text offers (optional)…</option>
              {[["rogers", "Rogers"], ["fido", "Fido"], ["bell", "Bell"], ["virgin", "Virgin Mobile"], ["telus", "Telus"], ["koodo", "Koodo"], ["freedom", "Freedom Mobile"]].map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          )}
          <input data-testid="sub-services" className="field-input w-full" placeholder="Services (comma separated: lawn care, brush removal…)" value={form.service_types} onChange={(e) => setForm({ ...form, service_types: e.target.value })} />
          <div className="flex gap-2">
            <input data-testid="sub-rate" type="number" className="field-input flex-1" placeholder="Rate $" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            <div className="flex gap-1">
              {["per_visit", "hourly"].map((b) => (
                <button type="button" key={b} data-testid={`basis-${b}`} onClick={() => setForm({ ...form, rate_basis: b })}
                  className={`h-12 rounded-md border px-3 text-xs font-bold ${form.rate_basis === b ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{b === "per_visit" ? "Per visit" : "Hourly"}</button>
              ))}
            </div>
          </div>
          <input data-testid="sub-email" type="email" className="field-input w-full" placeholder="Login email (optional — gives them app access)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {form.email && <input data-testid="sub-password" className="field-input w-full" placeholder="Temp password (optional — blank emails a 3-day setup link)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />}
          <button data-testid="sub-save" className="btn-primary-big !h-14 !text-base">Save</button>
        </form>
      )}
      <input data-testid="search-input" className="field-input mb-4 w-full" placeholder="Search crew name, phone, email…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-3" data-testid="roster-list">
        {!subs && <p className="text-muted-foreground">Loading…</p>}
        {subs?.filter((s) => !q || [s.name, s.phone, s.email].join(" ").toLowerCase().includes(q.toLowerCase())).map((s) => (
          <div key={s.id} className="card-flat space-y-3 p-4" data-testid={`sub-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-lg font-bold">{s.name} {s.user_id ? "" : <span className="text-xs font-normal text-muted-foreground">(no login)</span>}</p>
                <p className="text-sm text-muted-foreground">{s.phone} {s.phone && s.sms_carrier ? `(texts: ${s.sms_carrier}) ` : ""}{s.email && `· ${s.email}`}</p>
                {s.sms_opted_out && <span data-testid={`sub-opted-out-${s.id}`} className="mt-1 inline-block rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive">Opted out of texts</span>}
                {s.user_id && (s.last_login_at
                  ? <p className="text-xs text-muted-foreground" data-testid={`sub-seen-${s.id}`}>Last seen {fmtDate(s.last_login_at)}</p>
                  : <p className="text-xs font-bold text-[hsl(24,90%,42%)]" data-testid={`sub-invited-${s.id}`}>Invited — hasn't joined yet</p>)}
                <p className="mt-1 text-sm">{s.service_types.join(", ")} · <span className="font-semibold">{fmtMoney(s.rate)}/{s.rate_basis === "hourly" ? "hr" : "visit"}</span></p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button data-testid={`edit-sub-${s.id}`} onClick={() => startEdit(s)}
                  className="h-10 rounded-md border border-border px-3 text-xs font-bold">Edit</button>
                {s.email && (
                  <button data-testid={`sub-email-link-${s.id}`} onClick={() => emailLink(s)}
                    className="h-10 rounded-md border border-border px-3 text-xs font-bold">{s.last_login_at ? "Email reset link" : "Resend invite"}</button>
                )}
                <button data-testid={`sub-schedule-${s.id}`} onClick={() => setSchedId(schedId === s.id ? null : s.id)}
                  className="h-10 rounded-md border border-border px-3 text-xs font-bold">Send schedule</button>
                <button data-testid={`toggle-sub-${s.id}`} onClick={() => toggle(s)}
                  className={`h-10 rounded-md px-3 text-xs font-bold ${s.status === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {s.status.toUpperCase()}
                </button>
              </div>
            </div>
            {schedId === s.id && (
              <div className="space-y-2 rounded-md border border-border p-3" data-testid={`sub-schedule-panel-${s.id}`}>
                <p className="text-xs text-muted-foreground">Text and/or email {s.name} their assigned jobs and open offers for the next 7 days, in one list — no app login needed to read it.</p>
                <div className="flex gap-2">
                  {[["sms", "Text"], ["email", "Email"], ["both", "Both"]].map(([c, label]) => (
                    <button key={c} data-testid={`sub-schedule-send-${s.id}-${c}`} onClick={() => sendSchedule(s, c)}
                      className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground">{label}</button>
                  ))}
                </div>
              </div>
            )}
            {editId === s.id && (
              <div className="space-y-2 rounded-md border border-border p-3" data-testid={`edit-sub-form-${s.id}`}>
                <input data-testid={`edit-name-${s.id}`} className="field-input w-full" placeholder="Name" value={eform.name} onChange={(e) => setEform({ ...eform, name: e.target.value })} />
                <input data-testid={`edit-phone-${s.id}`} className="field-input w-full" placeholder="Phone" value={eform.phone} onChange={(e) => setEform({ ...eform, phone: e.target.value })} />
                {eform.phone && (
                  <select data-testid={`edit-carrier-${s.id}`} className="field-input w-full" value={eform.sms_carrier} onChange={(e) => setEform({ ...eform, sms_carrier: e.target.value })}>
                    <option value="">Carrier for text offers (optional)…</option>
                    {[["rogers", "Rogers"], ["fido", "Fido"], ["bell", "Bell"], ["virgin", "Virgin Mobile"], ["telus", "Telus"], ["koodo", "Koodo"], ["freedom", "Freedom Mobile"]].map(([v2, label]) => (
                      <option key={v2} value={v2}>{label}</option>
                    ))}
                  </select>
                )}
                <input data-testid={`edit-services-${s.id}`} className="field-input w-full" placeholder="Services (comma separated)" value={eform.service_types} onChange={(e) => setEform({ ...eform, service_types: e.target.value })} />
                <div className="flex gap-2">
                  <input data-testid={`edit-rate-${s.id}`} type="number" className="field-input flex-1" placeholder="Rate $" value={eform.rate} onChange={(e) => setEform({ ...eform, rate: e.target.value })} />
                  <div className="flex gap-1">
                    {["per_visit", "hourly"].map((b) => (
                      <button type="button" key={b} data-testid={`edit-basis-${s.id}-${b}`} onClick={() => setEform({ ...eform, rate_basis: b })}
                        className={`h-12 rounded-md border px-3 text-xs font-bold ${eform.rate_basis === b ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{b === "per_visit" ? "Per visit" : "Hourly"}</button>
                    ))}
                  </div>
                </div>
                <input data-testid={`edit-email-${s.id}`} type="email" className="field-input w-full" placeholder="Login email" value={eform.email} onChange={(e) => setEform({ ...eform, email: e.target.value })} />
                <div>
                  <p className="text-xs font-bold text-muted-foreground">Weekly availability</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
                      <button type="button" key={d} data-testid={`edit-avail-${s.id}-${d}`}
                        onClick={() => setEform({ ...eform, weekly: { ...eform.weekly, [d]: eform.weekly[d] === false } })}
                        className={`h-9 rounded-full border px-3 text-xs font-bold capitalize ${eform.weekly[d] !== false ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground line-through"}`}>{d}</button>
                    ))}
                  </div>
                  {(s.availability?.overrides || []).length > 0 && <p className="mt-1 text-xs text-muted-foreground">{s.availability.overrides.length} date exception(s) on file — the crew member manages these in their app</p>}
                </div>
                <input data-testid={`edit-password-${s.id}`} className="field-input w-full" placeholder="Set/reset app password (optional, 8+ chars)" value={eform.password} onChange={(e) => setEform({ ...eform, password: e.target.value })} />
                <button data-testid={`edit-sub-save-${s.id}`} onClick={() => saveEdit(s)} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save changes</button>
              </div>
            )}
            {s.doc_warnings?.length > 0 && (
              <div className="rounded-md bg-destructive/10 p-3" data-testid={`doc-warning-${s.id}`}>
                {s.doc_warnings.map((w) => <p key={w} className="flex items-center gap-1 text-xs font-bold text-destructive"><Warning size={14} weight="bold" /> {w}</p>)}
              </div>
            )}
            {s.documents?.length > 0 && (
              <div className="space-y-1">
                {s.documents.map((d) => (
                  <p key={d.id} className="text-xs text-muted-foreground">{d.type.toUpperCase()} · {d.filename} {d.expiry_date ? `· exp ${d.expiry_date}` : ""}</p>
                ))}
              </div>
            )}
            <details className="rounded-md border border-border p-3">
              <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold"><FileArrowUp size={16} weight="bold" /> Upload insurance / WSIB</summary>
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  {["insurance", "wsib", "other"].map((t) => (
                    <button type="button" key={t} data-testid={`doc-type-${s.id}-${t}`} onClick={() => setDocForm((p) => ({ ...p, [s.id]: { ...(p[s.id] || {}), type: t } }))}
                      className={`h-10 flex-1 rounded-md border text-xs font-bold uppercase ${docForm[s.id]?.type === t ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{t}</button>
                  ))}
                </div>
                <input data-testid={`doc-expiry-${s.id}`} className="field-input w-full" placeholder="Expiry date YYYY-MM-DD (optional)" value={docForm[s.id]?.expiry_date || ""}
                  onChange={(e) => setDocForm((p) => ({ ...p, [s.id]: { ...(p[s.id] || {}), expiry_date: e.target.value } }))} />
                <input data-testid={`doc-file-${s.id}`} type="file" className="text-sm" onChange={(e) => pickFile(s.id, e.target.files?.[0])} />
                <button data-testid={`doc-save-${s.id}`} onClick={() => uploadDoc(s.id)} className="h-12 w-full rounded-md bg-secondary text-sm font-bold text-secondary-foreground">Save document</button>
              </div>
            </details>
          </div>
        ))}
      </div>

      <section className="mt-8 space-y-3" data-testid="teams-section">
        <p className="label-caps">Crew teams (hourly members)</p>
        <p className="text-sm text-muted-foreground">Dispatch a team and every member is assigned together — each checks in/out themselves and is paid their own hours.</p>
        <form onSubmit={saveTeam} className="card-flat space-y-3 p-4" data-testid="team-form">
          <input data-testid="team-name" className="field-input w-full" placeholder="Team name (e.g. Saturday crew)" value={teamForm.name}
            onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} required />
          <div className="space-y-2">
            {(subs || []).filter((s) => s.status === "active" && s.rate_basis === "hourly").map((s) => (
              <button type="button" key={s.id} data-testid={`team-pick-${s.id}`}
                onClick={() => setTeamForm((f) => ({ ...f, member_ids: f.member_ids.includes(s.id) ? f.member_ids.filter((x) => x !== s.id) : [...f.member_ids, s.id] }))}
                className={`w-full rounded-md border p-3 text-left text-sm font-semibold transition-colors ${teamForm.member_ids.includes(s.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                {s.name} · {fmtMoney(s.rate)}/hr
              </button>
            ))}
            {(subs || []).filter((s) => s.status === "active" && s.rate_basis === "hourly").length === 0 && (
              <p className="text-xs text-muted-foreground">No active hourly crew yet — set a member's rate basis to Hourly to build a team.</p>
            )}
          </div>
          <div className="flex gap-2">
            <button data-testid="team-save" disabled={teamForm.member_ids.length < 2} className="h-12 flex-1 rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-40">
              {teamEditId ? "Save team" : "Create team"}
            </button>
            {teamEditId && <button type="button" data-testid="team-cancel-edit" onClick={() => { setTeamEditId(null); setTeamForm({ name: "", member_ids: [] }); }} className="h-12 rounded-md border border-border px-4 text-sm font-bold">Cancel</button>}
          </div>
        </form>
        <div className="space-y-2" data-testid="teams-list">
          {teams.map((t) => (
            <div key={t.id} className="card-flat flex items-center justify-between p-4" data-testid={`team-card-${t.id}`}>
              <div>
                <p className="font-semibold">{t.name}</p>
                <p className="text-xs text-muted-foreground">{(t.members || []).map((m) => `${m.name} (${fmtMoney(m.rate)}/hr)`).join(" · ")}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`team-edit-${t.id}`} onClick={() => { setTeamEditId(t.id); setTeamForm({ name: t.name, member_ids: t.member_ids }); }}
                  className="h-10 rounded-md border border-border px-3 text-xs font-bold">Edit</button>
                <button data-testid={`team-del-${t.id}`} onClick={() => deleteTeam(t.id)}
                  className="h-10 rounded-md border border-destructive px-3 text-xs font-bold text-destructive">Delete</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </Wrapper>
  );
}
